import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface ProtectedRouteProps {
    children: ReactNode;
    requireRoles?: string[];
    requireTeamRole?: { teamId: string; roles: string[] };
}

export default function ProtectedRoute({
    children,
    requireRoles,
    requireTeamRole,
}: ProtectedRouteProps): JSX.Element {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="protected-loading">
                <div className="loading-spinner">
                    <div className="spinner-ring" />
                    <p>Loading...</p>
                </div>
                <style>{`
          .protected-loading {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--color-bg);
          }

          .loading-spinner {
            text-align: center;
          }

          .spinner-ring {
            width: 48px;
            height: 48px;
            margin: 0 auto 1rem;
            border: 4px solid var(--color-border);
            border-top-color: var(--color-primary);
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .loading-spinner p {
            color: var(--color-text-muted);
            font-size: 0.875rem;
          }
        `}</style>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
    }

    // Check global roles
    if (requireRoles && requireRoles.length > 0) {
        const hasRequiredRole = requireRoles.some((role) =>
            user?.roles.includes(role)
        );

        if (!hasRequiredRole) {
            return (
                <div className="protected-forbidden">
                    <div className="forbidden-card">
                        <svg
                            width="64"
                            height="64"
                            viewBox="0 0 64 64"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="32" cy="32" r="28" stroke="var(--color-critical)" strokeWidth="2" />
                            <path
                                d="M32 20v16M32 44h.01"
                                stroke="var(--color-critical)"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                        <h1>Access Denied</h1>
                        <p>You don't have permission to access this page.</p>
                        <button
                            className="button secondary"
                            onClick={() => window.history.back()}
                        >
                            Go Back
                        </button>
                    </div>
                    <style>{`
            .protected-forbidden {
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              background: var(--color-bg);
              padding: 2rem;
            }

            .forbidden-card {
              text-align: center;
              max-width: 400px;
            }

            .forbidden-card svg {
              margin-bottom: 1.5rem;
            }

            .forbidden-card h1 {
              font-size: 1.75rem;
              margin-bottom: 0.5rem;
            }

            .forbidden-card p {
              color: var(--color-text-muted);
              margin-bottom: 2rem;
            }
          `}</style>
                </div>
            );
        }
    }

    // Check team-specific roles
    if (requireTeamRole) {
        const teamRoles = user?.teamRoles[requireTeamRole.teamId] ?? [];
        const hasTeamRole = requireTeamRole.roles.some((role) =>
            teamRoles.includes(role)
        );

        if (!hasTeamRole) {
            return (
                <div className="protected-forbidden">
                    <div className="forbidden-card">
                        <svg
                            width="64"
                            height="64"
                            viewBox="0 0 64 64"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="32" cy="32" r="28" stroke="var(--color-critical)" strokeWidth="2" />
                            <path
                                d="M32 20v16M32 44h.01"
                                stroke="var(--color-critical)"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                        <h1>Access Denied</h1>
                        <p>You don't have the required team permissions.</p>
                        <button
                            className="button secondary"
                            onClick={() => window.history.back()}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            );
        }
    }

    return <>{children}</>;
}
